import { coreGet, coreMutate, coreUpload, coreDownload } from "../../core-api/api-client";
export type Outcome = "PENDING" | "WON" | "RETURNED" | "FAILED";
export const outcomeNames: Record<Outcome, string> = {
  PENDING: "待处理",
  WON: "已成单",
  RETURNED: "已退货",
  FAILED: "已失败",
};
export const progressNames: Record<string, string> = {
  INQUIRY_CONFIRMED: "询单确认",
  INITIAL_SOURCING: "首次查货",
  INITIAL_QUOTING: "首次报价",
  SALES_FOLLOW_UP: "销售跟进",
  AWAITING_PO_CONFIRMATION: "待确认 PO",
  AWAITING_PAYMENT: "已付款",
  AWAITING_PROCUREMENT: "已采购",
};
export const automaticOptions = [
  "INQUIRY_CONFIRMED",
  "INITIAL_SOURCING",
  "INITIAL_QUOTING",
  "SALES_FOLLOW_UP",
];
export const manualOptions = ["AWAITING_PAYMENT", "AWAITING_PROCUREMENT"];
export type CaseReadChannel = "INTERNAL" | "EXTERNAL";
export interface CaseUnreadSummary {
  count: number;
  isUnread: boolean;
  manuallyUnread?: boolean;
  availableChannels: CaseReadChannel[];
  internalCount?: number;
  externalCount?: number;
}
export interface CaseReadState {
  unread: CaseUnreadSummary;
  internal?: {
    readThrough: { at: string; messageId: string } | null;
    firstUnreadMessageId: string | null;
  };
  external?: {
    readThrough: { at: string; messageId: string } | null;
    firstUnreadMessageId: string | null;
  };
}
export interface CaseSummary {
  id: string;
  caseNumber: string;
  subject: string;
  businessOutcome: Outcome;
  currentProgress: string | null;
  progressReviewRequired: boolean;
  rowVersion: number;
  openedAt: string;
  updatedAt?: string;
  firstInquiryAt: string;
  latestActivityAt: string;
  latestInternalMessage?: {
    id: string;
    text: string;
    createdAt: string;
  };
  latestExternalMessage?: {
    id: string;
    subject: string;
    direction: "INBOUND" | "OUTBOUND";
    createdAt: string;
  };
  personalTags?: Array<{ id: string; name: string }>;
  wonAt: string | null;
  returnedAt: string | null;
  failedAt: string | null;
  unread?: CaseUnreadSummary;
}
export interface CasePage {
  items: CaseSummary[];
  total: number;
  page: number;
  pageSize: number;
  hasNext: boolean;
  filter: {
    timezone?: string;
    field: string;
    from: string | null;
    to: string | null;
    allHistory: boolean;
  };
}
export interface CaseFilterOptions {
  metrics?: Array<{ key: string; name: string }>;
  permissions: {
    metrics: boolean;
    quotes: boolean;
    sourcing: boolean;
    tags: boolean;
  };
  tags: Array<{ id: string; name: string }>;
}
export interface RecentCaseUnreadSummary {
  count: number;
  from: string;
  generatedAt: string;
  byOutcome: Record<Outcome, number>;
}
export interface CaseMessage {
  id: string;
  clientMessageId: string | null;
  body: string;
  safeHtml: string | null;
  authorUserId: string;
  author: { id: string; displayName: string; avatarUrl: string | null };
  messageKind?: "USER" | "SYSTEM";
  systemEventType?: string | null;
  eventMetadata?: Record<string, unknown>;
  createdAt: string;
  version: number;
  readPosition?: { at: string; messageId: string };
}
export interface CaseExternalMessage {
  fileAttachments?: CaseMailAttachment[];
  id: string;
  conversationId?: string;
  subject: string;
  textBody: string | null;
  safeHtmlBody: string | null;
  direction: string;
  sender?: { email?: string; name?: string };
  recipients?: Array<{ email?: string; name?: string }>;
  createdAt: string;
  sentAt?: string | null;
  receivedAt?: string | null;
  readPosition?: { at: string; messageId: string };
  senderPresentation?: {
    displayName: string | null;
    occupation: string | null;
    mailboxEmail: string;
    mailboxRoleType?: string | null;
    sentByCurrentUser: boolean;
  } | null;
}
export type CaseMessageHistoryPage =
  | {
      channel: "INTERNAL";
      items: CaseMessage[];
      beforeCursor: string;
      hasMore: boolean;
    }
  | {
      channel: "EXTERNAL";
      items: CaseExternalMessage[];
      beforeCursor: string;
      hasMore: boolean;
    };
export interface CaseDetail extends CaseSummary {
  customMetrics?: Array<{
    key: string;
    name: string;
    shortName?: string;
    enabled: boolean;
  }>;
  automaticProgress: string | null;
  manualProgress: string[];
  legacyStatus: string | null;
  legacyProgress: string | null;
  failureSnapshot: unknown;
  customer: {
    primaryContact: { displayName: string; normalizedEmail: string } | null;
    customerOrganization: { displayName: string } | null;
  } | null;
  notes: Array<{
    id: string;
    clientMessageId?: string | null;
    body: string;
    safeHtml: string | null;
    authorUserId: string;
    author?: { id: string; displayName: string; avatarUrl: string | null };
    messageKind?: "USER" | "SYSTEM";
    systemEventType?: string | null;
    eventMetadata?: Record<string, unknown>;
    createdAt: string;
    version?: number;
    readPosition?: { at: string; messageId: string };
  }>;
  messageCursor?: string;
  messageHistory: {
    internal: { beforeCursor: string; hasMore: boolean };
    external: { beforeCursor: string; hasMore: boolean };
  };
  conversations: Array<{
    id: string;
    subject: string;
    emailMessages: CaseExternalMessage[];
  }>;
  replyContext?: {
    subject?: string;
    replyTarget: {
      messageId: string;
      conversationId: string;
      subject: string;
      receivedAt: string;
    };
    sender: {
      displayName: string;
      occupation: string | null;
      mailboxEmail: string;
      mailboxRoleType: string | null;
    };
    senders: Array<{
      mailboxId: string;
      mailboxEmail: string;
      mailboxRoleType: string | null;
      canSend: boolean;
      unavailableReason: string | null;
    }>;
    defaultBccEmail?: string;
    signature?: {
      configured: boolean;
      htmlBody: string;
      textBody: string;
      version: number;
      updatedAt: string | null;
    };
    recipient: { email: string; name: string | null } | null;
    canSend: boolean;
    unavailableReason: string | null;
  } | null;
  drafts: Array<{
    fileAttachments?: CaseMailAttachment[];
    id: string;
    subject: string;
    textBody: string;
    htmlBody: string | null;
    status: string;
    createdAt: string;
    senderDisplayName?: string | null;
    senderOccupation?: string | null;
    senderMailboxEmail?: string;
    senderMailboxRoleType?: string | null;
    recipients?: Array<{ email?: string; name?: string }>;
    senderMailboxAuthorizationStatus?: string;
    latestSendId?: string | null;
    latestSendStatus?: string | null;
    sendErrorCategory?: string | null;
  }>;
  sourcing: Array<{
    id: string;
    version: number;
    status: string;
    productReference: string | null;
    quantity: string;
    unit: string;
  }>;
  quotes: Array<{
    id: string;
    versionNumber: number;
    status: string;
    currency: string;
    total: string | null;
    subtotal: string;
    submittedAt: string | null;
  }>;
  finance: Array<{
    id: string;
    version: number;
    quoteVersionId: string;
    paymentInstructions: string;
    quoteSnapshot: unknown;
  }>;
  history: Array<{
    id: string;
    eventType: string;
    actorName?: string;
    actorUsername?: string | null;
    occurredAt: string;
    payload: {
      reason?: string;
      evidence?: string;
      actorUserId?: string;
      before?: unknown;
      after?: unknown;
    };
  }>;
  readState?: CaseReadState;
  isPlatformAdministrator?: boolean;
  permissions: Record<
    | "internalRead"
    | "internalSend"
    | "externalRead"
    | "externalSend"
    | "statusRead"
    | "statusChange"
    | "statusRestart"
    | "tagApply"
    | "tagManage"
    | "sourcingRead"
    | "sourcingManage"
    | "quoteRead"
    | "quoteManage"
    | "metricsRead"
    | "metricsManage"
    | "collaborationRead"
    | "collaborationManage"
    | "won"
    | "finance",
    boolean
  >;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  textBody: string;
  htmlBody: string;
  createdAt: string;
  updatedAt: string;
}

export type CaseCollaborationOrigin = "TEAM" | "ASSIGNEE" | "MANUAL";
export interface CaseCollaborationUser {
  userId: string;
  displayName: string;
  email: string | null;
  occupation: string;
}
export interface CaseCollaborationMember extends CaseCollaborationUser {
  origin: CaseCollaborationOrigin;
  removable: boolean;
  addedAt: string | null;
}
export interface CaseCollaboration {
  mode: "TEAM" | "ASSIGNEE";
  team: { id: string; name: string } | null;
  canManage: boolean;
  members: CaseCollaborationMember[];
  candidates: CaseCollaborationUser[];
}

export type SourcingProductOutcome =
  | "PENDING"
  | "FOUND"
  | "NO_SUPPLY"
  | "NEEDS_CLARIFICATION";
export interface SourcingPriceTier {
  id?: string;
  purity: string | null;
  leadTime: string | null;
  hasInventory: boolean;
  specUnit: string;
  specValue: string;
  priceValue: string | null;
  currency: string;
  validUntil: string;
}
export interface SourcingSupplierOption {
  id?: string;
  supplierId: string | null;
  supplierName: string;
  shippingConditions: string | null;
  shippingForm: string | null;
  isAlternative: boolean;
  isBestSupplier: boolean;
  isOverseasSupplier: boolean;
  remarks: string | null;
  priceTiers: SourcingPriceTier[];
}
export interface SourcingProduct {
  key: string;
  id?: string;
  sourceInboundInquiryId: string | null;
  productName: string;
  casNo: string | null;
  catalogNumber: string | null;
  requestedQuantity: string | null;
  specification: string | null;
  outcome: SourcingProductOutcome;
  noSupplyReason: string | null;
  clarificationNotes: string | null;
  sourceType: "CASE_INQUIRY" | "MANUAL" | "HISTORY_REUSE";
  reusedFromSourcingResultId: string | null;
  supplierOptions: SourcingSupplierOption[];
}
export interface SourcingHistoryCandidate {
  id: string;
  sourceCaseNumber: string;
  submittedAt: string;
  productReference: string | null;
  supplierId: string;
  supplierName: string;
  specification: string | null;
  requestedQuantity: string;
  priceTier: SourcingPriceTier;
}
export interface CaseSourcingDocument {
  id: string | null;
  version: number;
  status: "DRAFT" | "SUBMITTED" | "SUPERSEDED";
  sourceComment: string;
  lockVersion: number;
  submittedAt: string | null;
  updatedAt: string | null;
  products: SourcingProduct[];
}
export interface CaseSourcingDocumentResponse {
  exists: boolean;
  editable: boolean;
  canEdit: boolean;
  costVisible: boolean;
  document: CaseSourcingDocument;
  versions: CaseSourcingDocument[];
  summary: {
    total: number;
    found: number;
    noSupply: number;
    needsClarification: number;
    pending: number;
  };
  historyCandidates: Record<string, SourcingHistoryCandidate[]>;
  case?: {
    rowVersion: number;
    automaticProgress: string | null;
    currentProgress: string | null;
  };
}

export type CaseQuoteDocumentStatus = "DRAFT" | "SUBMITTED" | "SUPERSEDED";
export interface CaseQuoteLine {
  id?: string;
  sourceSourcingProductId: string | null;
  sourcePriceTierId: string | null;
  productName: string;
  catalogNumber: string;
  casNo: string;
  requestedQuantity: string;
  purity?: string;
  specification: string;
  quantity: string;
  specValue: string;
  specUnit: string;
  unitPrice: string;
  sourceUnitPrice?: string | null;
  sourceCurrency?: string | null;
  currency: string;
  leadTime: string;
  supplierName: string | null;
  costUnitPrice: string | null;
  costCurrency: string | null;
  costValidUntil: string | null;
  lineSubtotal: string | null;
  lineCost: string | null;
  grossProfit: string | null;
  grossMarginBps: number | null;
}
export interface CaseQuoteSourcingCandidate {
  line: CaseQuoteLine;
  supplierLabel: string | null;
  isBestSupplier: boolean;
  purity: string | null;
  validUntil: string | null;
  expired: boolean;
}
export interface CaseQuoteSourcingCandidates {
  sourcingVersion: number | null;
  candidates: CaseQuoteSourcingCandidate[];
}
export const loadQuoteSourcingCandidates = (caseId: string) =>
  coreGet<CaseQuoteSourcingCandidates>(
    `/case-workspace/${encodeURIComponent(caseId)}/quote-document/sourcing-candidates`,
  );

export interface CaseQuoteDocument {
  omitTotal?: boolean;
  customerQuoteNumber?: string;
  id: string | null;
  version: number;
  status: CaseQuoteDocumentStatus;
  lockVersion: number;
  basedOnDocumentId: string | null;
  quoteNumber: string;
  prefix: string;
  customerName: string;
  organization: string;
  contact: string;
  paymentTerms: string;
  country: string;
  shippingAddress: string;
  billingAddress: string;
  quoteDate: string;
  validUntil: string;
  currency: string;
  shippingFee: string;
  handlingFee: string;
  internalNotes: string;
  subtotal: string | null;
  totalCost: string | null;
  total: string | null;
  grossProfit: string | null;
  grossMarginBps: number | null;
  submittedAt: string | null;
  updatedAt: string | null;
  lines: CaseQuoteLine[];
}
export interface CaseQuoteHistoryCandidate {
  documentId: string;
  sourceCaseNumber: string;
  quoteNumber: string;
  version: number;
  submittedAt: string;
  currency: string;
  total: string | null;
  document: CaseQuoteDocument;
}
export interface CaseQuoteDocumentResponse {
  exists: boolean;
  editable: boolean;
  canEdit: boolean;
  canSubmit: boolean;
  costVisible: boolean;
  document: CaseQuoteDocument;
  versions: CaseQuoteDocument[];
  summary: {
    omitTotal?: boolean;
    version: number;
    status: CaseQuoteDocumentStatus;
    quoteNumber: string;
    customerName: string;
    itemCount: number;
    currency: string;
    total: string | null;
    grossProfit: string | null;
    grossMarginBps: number | null;
    validUntil: string;
    submittedAt: string | null;
  };
  historyCandidates: CaseQuoteHistoryCandidate[];
  case?: {
    rowVersion: number;
    automaticProgress: string | null;
    currentProgress: string | null;
  };
}

export interface GlobalSupplierChoice {
  identityId: string;
  supplierId: string | null;
  displayName: string;
  domain: string | null;
}

export const searchGlobalSuppliers = (search: string, signal?: AbortSignal) =>
  coreGet<GlobalSupplierChoice[]>(
    `/case-workspace/suppliers?search=${encodeURIComponent(search)}`,
    signal,
  );
export const resolveGlobalSupplier = (supplierIdentityId: string) =>
  coreMutate<GlobalSupplierChoice>(
    "/case-workspace/suppliers/resolve",
    "POST",
    { supplierIdentityId },
    `case-supplier-resolve-${crypto.randomUUID()}`,
  );
export const createGlobalSupplier = (body: { name: string; domain?: string }) =>
  coreMutate<GlobalSupplierChoice>(
    "/case-workspace/suppliers",
    "POST",
    body,
    `case-supplier-create-${crypto.randomUUID()}`,
  );

export const loadCaseSourcingDocument = (id: string, signal?: AbortSignal) =>
  coreGet<CaseSourcingDocumentResponse>(
    `/case-workspace/${encodeURIComponent(id)}/sourcing-document`,
    signal,
  );
export const saveCaseSourcingDocument = (
  id: string,
  body: Record<string, unknown>,
) =>
  coreMutate<CaseSourcingDocumentResponse>(
    `/case-workspace/${encodeURIComponent(id)}/sourcing-document`,
    "PUT",
    body,
    `case-sourcing-save-${crypto.randomUUID()}`,
  );
export const submitCaseSourcingDocument = (
  id: string,
  body: Record<string, unknown>,
) =>
  coreMutate<CaseSourcingDocumentResponse>(
    `/case-workspace/${encodeURIComponent(id)}/sourcing-document/submit`,
    "POST",
    body,
    `case-sourcing-submit-${crypto.randomUUID()}`,
  );
export const loadCaseQuoteDocument = (id: string, signal?: AbortSignal) =>
  coreGet<CaseQuoteDocumentResponse>(
    `/case-workspace/${encodeURIComponent(id)}/quote-document`,
    signal,
  );
export const saveCaseQuoteDocument = (
  id: string,
  body: Record<string, unknown>,
) =>
  coreMutate<CaseQuoteDocumentResponse>(
    `/case-workspace/${encodeURIComponent(id)}/quote-document`,
    "PUT",
    body,
    `case-quote-save-${crypto.randomUUID()}`,
  );
export const submitCaseQuoteDocument = (
  id: string,
  body: Record<string, unknown>,
) =>
  coreMutate<CaseQuoteDocumentResponse>(
    `/case-workspace/${encodeURIComponent(id)}/quote-document/submit`,
    "POST",
    body,
    `case-quote-submit-${crypto.randomUUID()}`,
  );
export const loadCasePage = (query: URLSearchParams, signal?: AbortSignal) =>
  coreGet<CasePage>(`/case-workspace?${query}`, signal);
export const loadCaseFilterOptions = (signal?: AbortSignal) =>
  coreGet<CaseFilterOptions>("/case-workspace/filter-options", signal);
export const loadRecentCaseUnreadSummary = (signal?: AbortSignal) =>
  coreGet<RecentCaseUnreadSummary>("/case-workspace/unread-summary", signal);
export const loadCaseWorkspace = (id: string, signal?: AbortSignal) =>
  coreGet<CaseDetail>(`/case-workspace/${encodeURIComponent(id)}`, signal);
export interface CaseMentionUser {
  id: string;
  displayName: string;
  username: string | null;
}
export const loadCaseMentionUsers = (id: string, signal?: AbortSignal) =>
  coreGet<CaseMentionUser[]>(
    `/case-workspace/${encodeURIComponent(id)}/mention-users`,
    signal,
  );
export const loadCaseCollaboration = (id: string, signal?: AbortSignal) =>
  coreGet<CaseCollaboration>(
    `/case-workspace/${encodeURIComponent(id)}/collaboration`,
    signal,
  );
export const addCaseCollaborationMember = (id: string, userId: string) =>
  coreMutate<CaseCollaboration>(
    `/case-workspace/${encodeURIComponent(id)}/collaboration/members`,
    "POST",
    { userId },
    `case-collaboration-add-${crypto.randomUUID()}`,
  );
export const removeCaseCollaborationMember = (id: string, userId: string) =>
  coreMutate<CaseCollaboration>(
    `/case-workspace/${encodeURIComponent(id)}/collaboration/members/${encodeURIComponent(userId)}`,
    "DELETE",
    {},
    `case-collaboration-remove-${crypto.randomUUID()}`,
  );
export const loadCaseMessages = (
  id: string,
  cursor = "",
  signal?: AbortSignal,
) => {
  const query = new URLSearchParams({ limit: "100" });
  if (cursor) query.set("cursor", cursor);
  return coreGet<{ items: CaseMessage[]; cursor: string; hasMore: boolean }>(
    `/case-workspace/${encodeURIComponent(id)}/messages?${query}`,
    signal,
  );
};
export const loadCaseMessageHistory = (
  id: string,
  channel: CaseReadChannel,
  before = "",
  signal?: AbortSignal,
) => {
  const query = new URLSearchParams({ channel, limit: "30" });
  if (before) query.set("before", before);
  return coreGet<CaseMessageHistoryPage>(
    `/case-workspace/${encodeURIComponent(id)}/message-history?${query}`,
    signal,
  );
};
export const appendCaseMessage = (
  id: string,
  body: { clientMessageId: string; html: string },
) =>
  coreMutate<CaseMessage>(
    `/case-workspace/${encodeURIComponent(id)}/messages`,
    "POST",
    body,
    `case-message-${body.clientMessageId}`,
  );
export const updateCaseReadState = (
  id: string,
  channel: CaseReadChannel,
  readThrough: { at: string; messageId: string },
) =>
  coreMutate<CaseReadState>(
    `/case-workspace/${encodeURIComponent(id)}/read-state`,
    "PUT",
    { channel, readThrough },
    `case-read-${id}-${channel}-${readThrough.messageId}`,
  );
export const markCaseUnread = (id: string) =>
  coreMutate<CaseReadState>(
    `/case-workspace/${encodeURIComponent(id)}/read-state/mark-unread`,
    "POST",
    {},
    `case-unread-${id}-${crypto.randomUUID()}`,
  );
export const markCaseRead = (id: string) =>
  coreMutate<CaseReadState>(
    `/case-workspace/${encodeURIComponent(id)}/read-state/mark-read`,
    "POST",
    {},
    `case-read-all-${id}-${crypto.randomUUID()}`,
  );
export const caseCommand = (
  id: string,
  body: Record<string, unknown>,
  key: string,
) =>
  coreMutate(
    `/case-workspace/${encodeURIComponent(id)}/commands`,
    "POST",
    body,
    key,
  );

export const sendCaseExternalMessage = (
  caseId: string,
  body: {
    fileAttachmentIds?: string[];
    mailboxId: string;
    replyToMessageId: string;
    html: string;
    bccEmail: string;
    includeSignature: boolean;
  },
  key: string,
) =>
  coreMutate<{ id: string; status: string; draftId: string }>(
    `/case-workspace/${encodeURIComponent(caseId)}/external-messages/send`,
    "POST",
    body,
    key,
  );

export interface RichTextImageUpload {
  id: string;
  filename: string;
  contentType: string;
  byteSize: number;
  src: string;
}

export interface CaseMailAttachment {
  id: string;
  filename: string;
  contentType: string;
  byteSize: number;
  quoteVersion: number | null;
  quoteDocumentId: string | null;
  downloadUrl: string;
}
export interface AttachmentQuoteVersion {
  id: string; quoteNumber: string; version: number;
  status: "DRAFT" | "SUBMITTED" | "SUPERSEDED"; submittedAt: string | null;
}
export const uploadCaseMailAttachment = (caseId: string, file: File) => {
  const form = new FormData(); form.append("file", file);
  return coreUpload<CaseMailAttachment>(`/case-workspace/${encodeURIComponent(caseId)}/mail-attachments`, form,
    `mail-attachment-${crypto.randomUUID()}`);
};
export const loadAttachmentQuoteVersions = (caseId: string) =>
  coreGet<AttachmentQuoteVersion[]>(`/case-workspace/${encodeURIComponent(caseId)}/mail-attachments/quote-versions`);
export const createQuoteAttachment = (caseId: string, documentId: string) =>
  coreMutate<CaseMailAttachment>(`/case-workspace/${encodeURIComponent(caseId)}/mail-attachments/quotation`, "POST", { documentId }, `quote-pdf-${crypto.randomUUID()}`);
export const downloadCaseMailAttachment = (file: CaseMailAttachment) => coreDownload(file.downloadUrl);
export const previewCaseMailAttachment = (file: CaseMailAttachment) =>
  coreDownload(`${file.downloadUrl}${file.downloadUrl.includes("?") ? "&" : "?"}preview=1`);

export const uploadCaseRichTextImage = (caseId: string, file: File) => {
  const form = new FormData();
  form.append("image", file);
  return coreUpload<RichTextImageUpload>(
    `/case-workspace/${encodeURIComponent(caseId)}/rich-text-images`,
    form,
    `case-rich-image-${crypto.randomUUID()}`,
  );
};

export const loadEmailTemplates = (signal?: AbortSignal) =>
  coreGet<EmailTemplate[]>("/case-workspace/email-templates", signal);

export const createEmailTemplate = (body: {
  name: string;
  subject: string;
  html: string;
}) =>
  coreMutate<EmailTemplate>(
    "/case-workspace/email-templates",
    "POST",
    body,
    `email-template-create-${crypto.randomUUID()}`,
  );

export const updateEmailTemplate = (
  id: string,
  body: { name: string; subject: string; html: string },
) =>
  coreMutate<EmailTemplate>(
    `/case-workspace/email-templates/${encodeURIComponent(id)}`,
    "PUT",
    body,
    `email-template-update-${id}-${crypto.randomUUID()}`,
  );

export const deleteEmailTemplate = (id: string) =>
  coreMutate<{ id: string; deleted: true }>(
    `/case-workspace/email-templates/${encodeURIComponent(id)}`,
    "DELETE",
    {},
    `email-template-delete-${id}-${crypto.randomUUID()}`,
  );

export const draftAction = (
  caseId: string,
  id: string,
  action: "submit" | "approve" | "send" | "retry" | "withdraw",
  key: string,
) =>
  coreMutate(
    `/case-workspace/${encodeURIComponent(caseId)}/drafts/${encodeURIComponent(id)}/${action}`,
    "POST",
    {},
    key,
  );
